import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bot, Plus, Play, Pause, Settings, MessageSquare, Trash2, AlertCircle,
  BrainCircuit, Cpu, Activity, CheckCircle
} from 'lucide-react';
import agentService from '../../api/services/agentService';
import { useLang } from '../../i18n/LanguageContext';

export default function Agents() {
  const navigate = useNavigate();
  const { t } = useLang();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingAgentId, setUpdatingAgentId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);

  useEffect(() => { loadAgents(); }, []);

  const loadAgents = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await agentService.getAgents();
      if (response.success && response.data) {
        const list = Array.isArray(response.data) ? response.data : (response.data.agents || []);
        setAgents(list);
      }
      setLoading(false);
    } catch (err) {
      setError(err.message || t.agents.loadError);
      setLoading(false);
    }
  };

  const toggleAgentStatus = async (agentId, currentStatus) => {
    if (updatingAgentId) return;
    try {
      setUpdatingAgentId(agentId);
      const newStatus = currentStatus === 'active' ? 'paused' : 'active';
      await agentService.updateAgent(agentId, { status: newStatus });
      await new Promise(r => setTimeout(r, 100));
      await loadAgents();
    } catch (err) {
      loadAgents();
    } finally {
      setUpdatingAgentId(null);
    }
  };

  const confirmDelete = (agent) => { setAgentToDelete(agent); setDeleteConfirm(true); };

  const handleDelete = async () => {
    if (!agentToDelete) return;
    try {
      await agentService.deleteAgent(agentToDelete.id);
      setDeleteConfirm(false);
      setAgentToDelete(null);
      await loadAgents();
    } catch {
      alert(t.agents.deleteError);
    }
  };

  const totalAgents = agents.length;
  const agentsActifs = agents.filter(a => a.status === 'active').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-10 w-10 mx-auto mb-4"
            style={{ borderWidth: '2px', borderStyle: 'solid', borderColor: 'var(--c-border)', borderTopColor: 'rgb(var(--c-accent-green-rgb))' }}
          />
          <p className="text-label">{t.agents.loading}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card p-5 mb-4" style={{ borderColor: 'rgba(224,84,84,0.3)' }}>
        <div className="flex items-start gap-3">
          <AlertCircle size={16} style={{ color: 'var(--c-error)', flexShrink: 0, marginTop: 2 }} />
          <div>
            <p className="text-sm font-medium" style={{ color: 'var(--c-error)' }}>{t.common.error}</p>
            <p className="text-label mt-1">{error}</p>
            <button onClick={loadAgents} className="btn-primary mt-3" style={{ height: '32px', fontSize: '12px' }}>
              {t.common.retry}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* En-tête de page */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs2 mb-1" style={{ color: 'var(--c-text-muted)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {t.agents.title}
          </p>
          <h1 className="text-ink font-semibold" style={{ fontSize: '18px' }}>
            {t.agents.subtitle}
          </h1>
        </div>
        <button className="btn-primary" onClick={() => navigate('/agents/create')}>
          <Plus size={14} strokeWidth={2} />
          {t.agents.create}
        </button>
      </div>

      {/* Métriques globales */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="card-metric p-5">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center mb-3"
            style={{ background: 'rgba(var(--c-accent-green-rgb), 0.12)' }}
          >
            <BrainCircuit size={18} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} strokeWidth={1.5} />
          </div>
          <p className="text-label mb-1">{t.agents.total}</p>
          <p className="font-metric text-ink" style={{ fontSize: '28px', fontWeight: 600, lineHeight: 1 }}>
            {totalAgents}
          </p>
        </div>

        <div className="card-metric p-5">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center mb-3"
            style={{ background: 'rgba(var(--c-accent-green-rgb), 0.12)' }}
          >
            <Activity size={18} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} strokeWidth={1.5} />
          </div>
          <p className="text-label mb-1">{t.agents.active}</p>
          <p className="font-metric text-ink" style={{ fontSize: '28px', fontWeight: 600, lineHeight: 1 }}>
            {agentsActifs}
          </p>
        </div>
      </div>

      {/* Titre liste */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-medium text-ink flex items-center gap-2">
          <Bot size={15} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} strokeWidth={1.5} />
          Liste des agents
          {agents.length > 0 && (
            <span
              className="menu-badge"
              style={{ fontSize: '10px', padding: '1px 6px' }}
            >
              {agents.length}
            </span>
          )}
        </h2>
      </div>

      {/* État vide */}
      {agents.length === 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="card p-12 text-center"
        >
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4"
            style={{ background: 'rgba(var(--c-accent-green-rgb), 0.08)' }}
          >
            <BrainCircuit size={32} style={{ color: 'var(--c-text-muted)' }} strokeWidth={1.5} />
          </div>
          <h3 className="text-ink font-medium mb-2" style={{ fontSize: '16px' }}>
            {t.agents.noAgents}
          </h3>
          <p className="text-label mb-6 max-w-sm mx-auto">
            {t.agents.noAgentsDesc}
          </p>
          <button className="btn-primary" onClick={() => navigate('/agents/create')}>
            <Plus size={14} strokeWidth={2} />
            {t.agents.createFirst}
          </button>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {agents.map((agent, index) => {
            const agentKey = agent.id || agent._id || agent.name;
            const isActive = agent.status === 'active';
            const isUpdating = updatingAgentId === agentKey;

            return (
              <motion.div
                key={agentKey}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.06, duration: 0.3, ease: 'easeOut' }}
                className="card relative overflow-hidden"
                style={{ padding: '20px' }}
              >
                {/* Barre de statut gauche */}
                <div
                  className="absolute left-0 top-0 bottom-0 w-0.5"
                  style={{
                    background: isActive
                      ? 'rgb(var(--c-accent-green-rgb))'
                      : 'var(--c-border)',
                  }}
                />

                {/* Header */}
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{
                        background: isActive
                          ? 'rgba(var(--c-accent-green-rgb), 0.12)'
                          : 'rgba(138,138,138,0.1)',
                      }}
                    >
                      <BrainCircuit
                        size={20}
                        strokeWidth={1.5}
                        style={{
                          color: isActive
                            ? 'rgb(var(--c-accent-green-rgb))'
                            : 'var(--c-text-muted)',
                        }}
                      />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-medium text-ink truncate">{agent.name}</h3>
                      <div className="flex items-center gap-1.5 mt-1">
                        <div
                          className="w-1.5 h-1.5 rounded-full"
                          style={{
                            background: isActive
                              ? 'rgb(var(--c-accent-green-rgb))'
                              : 'var(--c-warning)',
                          }}
                        />
                        <span className="text-label">
                          {isActive ? t.agents.statusActive : t.agents.statusPaused}
                        </span>
                        {agent.type && (
                          <>
                            <span className="text-label">·</span>
                            <span className="text-label">{agent.type}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-0.5 flex-shrink-0">
                    <button
                      onClick={() => navigate(`/agents/${agentKey}`)}
                      className="p-1.5 rounded-lg cursor-pointer"
                      style={{ color: 'var(--c-text-gray)' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'var(--c-hover)'; e.currentTarget.style.color = 'rgb(var(--c-accent-green-rgb))'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--c-text-gray)'; }}
                      title={t.agents.configure}
                      aria-label={t.agents.configure}
                    >
                      <Settings size={15} strokeWidth={1.5} />
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); confirmDelete(agent); }}
                      className="p-1.5 rounded-lg cursor-pointer"
                      style={{ color: 'var(--c-text-gray)' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(224,84,84,0.1)'; e.currentTarget.style.color = 'var(--c-error)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--c-text-gray)'; }}
                      title={t.common.delete}
                      aria-label={t.common.delete}
                    >
                      <Trash2 size={15} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>

                {/* Métriques */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div
                    className="rounded-lg p-3"
                    style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <MessageSquare size={12} strokeWidth={1.5} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                      <span className="text-label">{t.agents.conversations}</span>
                    </div>
                    <p className="font-metric text-ink" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {agent.conversationCount || 0}
                    </p>
                  </div>

                  <div
                    className="rounded-lg p-3"
                    style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <CheckCircle size={12} strokeWidth={1.5} style={{ color: 'var(--c-info)' }} />
                      <span className="text-label">Performance</span>
                    </div>
                    <p className="font-metric text-ink" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {agent.performance || 95}%
                    </p>
                  </div>
                </div>

                {/* Modèle IA */}
                {agent.model && (
                  <div
                    className="flex items-center gap-2 mb-4 rounded-lg px-3 py-2"
                    style={{ background: 'var(--c-primary-dark)', border: '0.5px solid var(--c-border)' }}
                  >
                    <Cpu size={13} strokeWidth={1.5} style={{ color: 'rgb(var(--c-accent-green-rgb))' }} />
                    <span className="text-label">Modèle</span>
                    <span className="text-sm text-ink font-medium truncate">{agent.model}</span>
                  </div>
                )}

                {/* Bouton activation / pause */}
                <button
                  type="button"
                  onClick={() => toggleAgentStatus(agentKey, agent.status)}
                  disabled={updatingAgentId !== null}
                  className="w-full flex items-center justify-center gap-2 rounded-lg py-2.5 cursor-pointer"
                  style={{
                    fontSize: '13px',
                    fontWeight: 500,
                    border: '0.5px solid',
                    borderColor: isUpdating
                      ? 'var(--c-border)'
                      : isActive
                        ? 'rgba(229,162,53,0.3)'
                        : 'rgba(var(--c-accent-green-rgb), 0.3)',
                    background: isUpdating
                      ? 'transparent'
                      : isActive
                        ? 'rgba(229,162,53,0.08)'
                        : 'rgba(var(--c-accent-green-rgb), 0.08)',
                    color: isUpdating
                      ? 'var(--c-text-muted)'
                      : isActive
                        ? 'var(--c-warning)'
                        : 'rgb(var(--c-accent-green-rgb))',
                    opacity: updatingAgentId !== null && !isUpdating ? 0.5 : 1,
                    cursor: updatingAgentId !== null ? 'not-allowed' : 'pointer',
                  }}
                >
                  {isUpdating ? (
                    <>
                      <div
                        className="w-3.5 h-3.5 rounded-full animate-spin"
                        style={{ border: '1.5px solid currentColor', borderTopColor: 'transparent' }}
                      />
                      {t.common.loading}
                    </>
                  ) : isActive ? (
                    <><Pause size={14} strokeWidth={1.5} /> {t.agents.statusPaused}</>
                  ) : (
                    <><Play size={14} strokeWidth={1.5} /> {t.agents.statusActive}</>
                  )}
                </button>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Modal confirmation suppression */}
      <AnimatePresence>
        {deleteConfirm && agentToDelete && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.5)', zIndex: 'var(--z-modal)' }}
            onClick={() => setDeleteConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="glass max-w-sm w-full rounded-xl p-6"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center gap-3 mb-4">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(224,84,84,0.12)' }}
                >
                  <AlertCircle size={18} style={{ color: 'var(--c-error)' }} />
                </div>
                <div>
                  <p className="text-sm font-medium text-ink">{t.agents.deleteConfirmTitle}</p>
                  <p className="text-label">{t.agents.deleteConfirmDesc(agentToDelete.name)}</p>
                </div>
              </div>

              <div
                className="rounded-lg p-4 mb-5"
                style={{ background: 'rgba(224,84,84,0.06)', border: '0.5px solid rgba(224,84,84,0.2)' }}
              >
                <p className="text-sm text-ink mb-2">
                  Vous allez supprimer <strong>{agentToDelete.name}</strong>.
                </p>
                <ul className="text-label space-y-1" style={{ color: 'var(--c-error)', opacity: 0.8 }}>
                  <li>· Historique des conversations</li>
                  <li>· Base de connaissances</li>
                  <li>· Métriques et configuration</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteConfirm(false)}
                  className="flex-1 py-2 rounded-lg text-sm font-medium text-ink cursor-pointer"
                  style={{ background: 'var(--c-hover)', border: '0.5px solid var(--c-border)' }}
                >
                  {t.common.cancel}
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 py-2 rounded-lg text-sm font-medium text-white flex items-center justify-center gap-2 cursor-pointer"
                  style={{ background: 'var(--c-error)' }}
                >
                  <Trash2 size={14} strokeWidth={1.5} />
                  {t.agents.deleteConfirmBtn}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
